import { useStore } from 'vuex'
import { computed, ref, onMounted, watch } from 'vue'
import deepClone from 'foundation/js/deepClone'
import SearchResultCard from './subcomponents/SearchResultCard'
import RecipeCard from './subcomponents/RecipeCard'
import ActivityCard from '../ActivityCard/ActivityCard.vue'
import FrivilligjobCard from '../FrivilligjobCard/FrivilligjobCard.vue'
import LocationCard from '../LocationCard/LocationCard.vue'
import SortDropdown from '../SortDropdown/SortDropdown.vue'
import FundraisingCard from '../FundraisingCard/FundraisingCard.vue'
import DonationCard from '../DonationCard/DonationCard.vue'
import SimpleCard from '../SimpleCard/SimpleCard.vue'
import SpotCard from '../SpotCard/SpotCard.vue'
import GalleryCard from '../GalleryCard/GalleryCard.vue'
import SpotCardGrid from '../SpotCardGrid/SpotCardGrid.vue'
import eventDataAdapter from './subcomponents/eventDataAdapter'
import { GetLayoutStyle } from "./subcomponents/GeneralSearchLayoutEnums";
const sortOptions = {
    fundraising: {
        name: 'Sorter',
        options: [
            {
                name: 'Senest oprettet',
                handler: 'latest'
            },
            {
                name: 'Flest indsamlet penge',
                handler: 'highestCollected'
            }
        ]
    }
}

const urlMap = {
    counselling: 'COUNSELLING_SEARCH',
    events: 'EVENT_SEARCH',
    fundraising: 'FUNDRAISING_SEARCH',
    donations: 'DONATION_SEARCH',
    gallery: 'GALLERY_SEARCH',
    patientAssociations: 'PATIENT_ASSOCIATIONS_SEARCH',
    jobs: 'FRIVILLIGJOB_SEARCH',
    cancerforum: 'CANCERFORUM_SEARCH'
}

const getUrl = function (type, store) {
    return urlMap[type] ?? 'GENERAL_SEARCH'
}

const generalSearch = function (props, store) {
    store.dispatch('search/initSearchScope', {
        guid: props.guid,
        firstPageSize: props.firstPageSize,
        showMorePageSize: props.showMorePageSize,
        url: process.env[
            getUrl(props.type, store).replace(
                '[pageId]',
                store.state.main.page_id
            )
        ]
    })

    const inputText = new URLSearchParams(document.location.search).get('q')
    const skip = new URLSearchParams(document.location.search).get('skip')
    const take =
        new URLSearchParams(document.location.search).get('take') ?? props.take
    store.dispatch('search/performZeroSearch', {
        guid: props.guid
    })

    store.dispatch('search/performSearch', {
        guid: props.guid,
        query: inputText,
        skip,
        take
    })
}

export default {
    name: 'GeneralSearch',
    components: {
        SearchResultCard,
        RecipeCard,
        ActivityCard,
        FrivilligjobCard,
        LocationCard,
        FundraisingCard,
        SortDropdown,
        DonationCard,
        GalleryCard,
        SimpleCard,
        SpotCard,
        SpotCardGrid
    },
    props: {
        guid: {
            type: String,
            required: true
        },
        type: {
            type: String,
            required: false,
            default: 'general'
        },
        firstPageSize: {
            type: Number,
            required: false
        },
        showMorePageSize: {
            type: Number,
            required: false
        },
        spotCardData: {
            type: Object,
            required: false
        },
        hideNoResults: {
            type: Boolean,
            required: false,
            default: false
        },
        avoidSetQueryInUrl: {
            type: Boolean,
            required: false,
            default: false
        },
        isMultisearch: {
            type: Boolean,
            required: false,
            default: false
        },
        groupedContent: {
            type: String,
            required: false,
            default: 'false'
        },
        layoutStyle: {
            type: String,
            required: false,
            default: ''
        },
    },
    setup(props) {
        const inputText = computed(
            () => store.state.search.queries[props.guid] ?? ''
        )
        const store = useStore()
        const results = computed(() => {
            const data =
                props.type === 'events'
                    ? eventDataAdapter(
                          deepClone(
                              store.state.search.results[props.guid] ?? []
                          )
                      )
                    : deepClone(store.state.search.results[props.guid] ?? [])

            if (props.type !== 'events' || !props.groupedContent) return data

            const spotGroups = data.filter((g) => g?.isSpotCard)
            const nonSpotGroups = data.filter((g) => !g?.isSpotCard)

            if (!nonSpotGroups.length) return spotGroups

            const spotEntries = spotGroups
                .flatMap((g) => g?.entries ?? [])
                .slice()
                .sort(
                    (a, b) =>
                        (Number(a?.position) || 999999) -
                        (Number(b?.position) || 999999)
                )

            const baseEventCount = nonSpotGroups.reduce(
                (sum, g) => sum + (g?.entries?.length ?? 0),
                0
            )

            const insertAtGlobalIndex = (entry, globalIndex) => {
                let idx = globalIndex

                for (const group of nonSpotGroups) {
                    const entries = group.entries ?? (group.entries = [])
                    if (idx <= entries.length) {
                        entries.splice(idx, 0, entry)
                        return
                    }
                    idx -= entries.length
                }
                nonSpotGroups[nonSpotGroups.length - 1].entries.push(entry)
            }
            let insertedSoFar = 0

            for (const spot of spotEntries) {
                const pos = Number(spot?.position)

                const baseInsertIndex =
                    Number.isFinite(pos) && pos > 0
                        ? Math.min(pos - 1, baseEventCount)
                        : baseEventCount

                const globalInsertIndex = baseInsertIndex + insertedSoFar
                insertAtGlobalIndex(spot, globalInsertIndex)
                insertedSoFar += 1
            }

            return nonSpotGroups
        })

        const isOnlySpotCardsInGroupedEvents = computed(() => {
            const isGroupedEvents =
                props.type === 'events' && !!props.groupedContent
            return (
                isGroupedEvents &&
                hasAnyResults.value &&
                resultCount.value === 0
            )
        })

        const hasAnyResults = computed(() => {
            if (!results.value) return false

            return (
                results.value.reduce((count, result) => {
                    if (Array.isArray(result.entries))
                        return count + result.entries.length
                    return count + 1
                }, 0) > 0
            )
        })

        const spotCardCount = computed(() => {
            return results.value.filter((r) => r.isSpotCard).length
        })

        const resultCount = computed(() => {
            if (!results.value) return 0

            const total = results.value.reduce((count, result) => {

                if (Array.isArray(result.entries)) {
                    return count + result.entries.filter(entry => !entry.isSpotCard).length
                }

                if (result.isSpotCard) {
                    return count
                }

                return count + 1
            }, 0)
            return total
        })

        const totalCount = computed(() => {
            return (
                store.state.search.scoped_total[props.guid] -
                    spotCardCount.value ?? 0
            )
        })

        watch(totalCount, (val) => {
            store.commit('search/set_display_total', { guid: props.guid, data: val })
        }, { immediate: true })

        const tags = computed(
            () => store.getters['search/selectedTags'](props.guid) ?? []
        )
        const showSpinner = computed(() => store.state.search.is_searching)
        const searchLabelCollection = props.isMultisearch
            ? `generalSearch-${props.guid}`
            : 'generalSearch'
        const getLabel = (labelKey, params) =>
            store.getters['labels/getLabel']({
                labelCollection: searchLabelCollection,
                labelKey: labelKey,
                params: params
            })
        const existLabel = (labelKey) =>
            store.getters['labels/existLabel']({
                labelCollection: searchLabelCollection,
                labelKey: labelKey
            })

        const getMoreResults = function () {
            store.dispatch('search/performMoreSearch', {
                guid: props.guid
            })
        }

        onMounted(() => {
            if (props.avoidSetQueryInUrl) {
                store.commit('search/set_replaceQueryInUrl', false)
            }

            if (props.guid === 'dictionarySearch') {
                store.dispatch('search/initSearchScope', {
                    guid: props.guid,
                    url: null
                })
            } else {
                generalSearch(props, store)
            }
        })

        const getResultImageRatio = function (result) {
            if (!result || !result.documentType) return null

            return (
                {
                    NewsPage: '3-2',
                    LongreadPage: '3-2',
                    PodcastPage: '1-1',
                    RecipePage: '4-5',
                    ReportPage: 'A4'
                }[result.documentType] ?? null
            )
        }

        const getResultImageType = function (result) {
            if (!result || !result.documentType) return null

            return (
                {
                    PodcastPage: 'podcast',
                    RecipePage: 'recipe',
                    ReportPage: 'report'
                }[result.documentType] ?? null
            )
        }

        const clearFilterOnTag = function (tag) {
            store.dispatch('search/performSearch', {
                guid: props.guid,
                query: inputText.value,
                facet: tag
            })
        }

        const sortingHandler = function (sorting) {
            store.dispatch('search/performSearch', {
                guid: props.guid,
                sorting
            })
        }

        const canShowMore = computed(() =>
            store.getters['search/canShowMore'](props.guid)
        )

        // if list is type with grid, class placed on parent in order to hit meta-top-container too
        const gridTypeList = () => {
            // recipe grid has unique 2 col mobile grid, hence modifier
            if (props.type === 'recipe') {
                return 'kb-grid-list kb-grid-list--recipe'
            } else if (props.type === 'counselling' || 'fundraising') {
                return 'kb-grid-list'
            }
        }

        const displayProperties = {
            news: {
                card: 'SearchResultCard',
                grid: false,
                date: true,
                showCardTitle: true,
                summary: { hidable: true },
                image: true,
                breadcrumbs: false
            },
            recipe: { card: 'RecipeCard', grid: true, listType: '4col' },
            events: {
                card: 'ActivityCard',
                grid: false,
                listType: 'fixed-header'
            },
            donations: {
                card: 'DonationCard',
                grid: false,
                hideTotalCount: true,
                listType: 'content-width'
            },
            gallery: {
                card: 'GalleryCard',
                grid: false,
                hideTotalCount: false
            },
            fundraising: {
                card: 'FundraisingCard',
                grid: true,
                listType: '3col',
                sortOptions: sortOptions['fundraising'],
                layout: GetLayoutStyle(props.layoutStyle)
            },
            counselling: {
                card: 'LocationCard',
                grid: true,
                hideTotalCount: true,
                hideTags: true,
                showSearchTitleCount: true,
                listType: '3col'
            },
            patientAssociations: {
                card: 'SimpleCard',
                grid: true,
                listType: '3col'
            },
            documentList: { card: 'SimpleCard', grid: true, listType: '3col' },
            jobs: {
                card: 'FrivilligjobCard',
                grid: false,
                hideTotalCount: false,
                hideTags: false,
                showSearchTitleCount: true,
                showCtaButton: true
            }
        }[props.type] ?? {
            card: 'SearchResultCard',
            grid: false,
            date: false,
            showCardTitle: true,
            summary: true,
            image: true,
            breadcrumbs: true
        }

        const displayModifications = computed(() => {
            const grid = 'kb-general-search__results--grid'
            const list = 'kb-general-search__results--list'
            const gridModifications =
                displayProperties.grid && displayProperties.listType
                    ? displayProperties.listType
                        ? grid + '-' + displayProperties.listType
                        : ''
                    : null
            const listModifications =
                !displayProperties.grid && displayProperties.listType
                    ? list + '-' + displayProperties.listType
                    : null

            return gridModifications
                ? gridModifications
                : listModifications
                  ? listModifications
                  : ''
        })

        const spotCardComponent = displayProperties.grid
            ? SpotCardGrid
            : SpotCard

        const totalCountLabel = computed(() =>
            getLabel('resultCountTitle').replace('{}', totalCount.value)
        )
        const searchTitleCountLabel = computed(() =>
            getLabel('searchTitleCount')?.replace('{}', totalCount.value)
        )

        // Views other than the activity and site search ones still ship no resultOutForTitle, so fall back to the sentence it replaced.
        const resultOutForLabel = computed(() => existLabel('resultOutForTitle')
            ? getLabel('resultOutForTitle', [resultCount.value, totalCount.value, getLabel('resultTypeTitle')])
            : `Viser ${resultCount.value} ud af ${totalCount.value} ${getLabel('resultTypeTitle')}`
        )

        return {
            displayProperties,
            results,
            resultCount,
            tags,
            getLabel,
            getMoreResults,
            clearFilterOnTag,
            gridTypeList,
            showSpinner,
            totalCount,
            totalCountLabel,
            searchTitleCountLabel,
            resultOutForLabel,
            canShowMore,
            sortingHandler,
            displayModifications,
            spotCardComponent,
            canShowMore,
            getResultImageRatio,
            getResultImageType,
            existLabel,
            hasAnyResults,
            isOnlySpotCardsInGroupedEvents
        }
    }
}
